import { useEffect, useRef, useState } from "react";
import { Upload, FileSpreadsheet, FileDown, Loader2, Undo2, AlertTriangle, Mail } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api, inr, formatApiErrorDetail } from "@/lib/api";
import { PageHeader, Panel, PillTabs, StatCard } from "@/components/crm/common";
import { useAuth } from "@/context/AuthContext";

const TYPES = ["Contract value", "Client payment", "Vendor expense", "Internal expense", "Studio overhead"];
const NEW = "__new__";

async function downloadBlob(promise, fallback) {
  try {
    const res = await promise;
    const name = /filename="([^"]+)"/.exec(res.headers["content-disposition"] || "")?.[1] || fallback;
    const a = document.createElement("a");
    a.href = URL.createObjectURL(res.data);
    a.download = name;
    a.click();
  } catch (err) {
    const d = err.response?.data instanceof Blob ? JSON.parse(await err.response.data.text()).detail : err.response?.data?.detail;
    toast.error(formatApiErrorDetail(d) || "Download failed");
  }
}

export function ReportView({ report, rows, title, testid = "fin-report", canEmail = false }) {
  const s = report.summary;
  const [sending, setSending] = useState(false);
  const emailMe = async () => {
    setSending(true);
    try { const { data } = await api.post("/finance-report/email"); toast.success(`Report emailed to ${data.sent_to} (Excel + PDF)`); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setSending(false);
  };
  const exp = (format) => downloadBlob(api.post("/finance-report/export", { report, rows, format, title }, { responseType: "blob" }), `finance-report.${format}`);
  return (
    <div className="space-y-5" data-testid={testid}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-lg font-bold text-zinc-900">{title}</h2>
        <div className="flex flex-wrap gap-2">
          {canEmail && <Button data-testid={`${testid}-email`} size="sm" variant="outline" disabled={sending} onClick={emailMe}>{sending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Mail className="mr-1 h-4 w-4" />}Email report to Joy</Button>}
          <Button data-testid={`${testid}-excel`} size="sm" variant="outline" onClick={() => exp("xlsx")}><FileSpreadsheet className="mr-1 h-4 w-4" />Download Excel</Button>
          <Button data-testid={`${testid}-pdf`} size="sm" variant="outline" onClick={() => exp("pdf")}><FileDown className="mr-1 h-4 w-4" />Download PDF</Button>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <StatCard testid={`${testid}-revenue`} label="Revenue" value={inr(s.revenue)} hint={`Contract ${inr(s.contract)}`} />
        <StatCard testid={`${testid}-received`} label="Received from clients" value={inr(s.received)} hint={`Receivable ${inr(s.receivable)}`} />
        <StatCard testid={`${testid}-project-exp`} label="Project expenses" value={inr(s.project_expenses)} hint={`Vendor ${inr(s.vendor)} · Internal ${inr(s.internal)}`} />
        <StatCard testid={`${testid}-overhead`} label="Studio overheads" value={inr(s.overhead)} />
        <StatCard testid={`${testid}-total-exp`} label="Overall expenses" value={inr(s.total_expenses)} />
        <StatCard testid={`${testid}-net-profit`} label="Net studio profit" value={inr(s.net_profit)} tone={s.net_profit < 0 ? "text-rose-600" : "text-violet-700"} hint={`Margin ${s.net_margin}%`} />
      </div>
      <Panel title="Project-wise profit & loss" testid={`${testid}-projects`}>
        <div className="thin-scroll overflow-x-auto">
          <table className="w-full min-w-[820px] text-sm">
            <thead><tr className="border-b border-zinc-200 text-left text-[11px] uppercase tracking-wider text-zinc-400">
              {["Project", "Contract", "Received", "Vendor", "Internal", "Total exp.", "Profit", "Margin"].map((h, i) => <th key={h} className={`py-2 ${i ? "text-right" : ""}`}>{h}</th>)}
            </tr></thead>
            <tbody>
              {report.projects.map((p) => (
                <tr key={p.project} data-testid={`${testid}-row-${p.project}`} className="border-b border-zinc-100">
                  <td className="py-2"><div className="font-semibold text-zinc-900">{p.project}</div><div className="text-xs text-zinc-400">{p.client}</div></td>
                  {[p.contract, p.received, p.vendor, p.internal, p.expenses].map((v, i) => <td key={i} className="py-2 text-right">{inr(v)}</td>)}
                  <td className={`py-2 text-right font-semibold ${p.profit < 0 ? "text-rose-600" : "text-zinc-900"}`}>{inr(p.profit)}</td>
                  <td className="py-2 text-right text-zinc-500" title={`Based on ${p.revenue_basis}`}>{p.margin}%</td>
                </tr>
              ))}
              {!report.projects.length && <tr><td colSpan={8} className="py-6 text-center text-zinc-400">No project entries.</td></tr>}
            </tbody>
          </table>
        </div>
      </Panel>
      {report.overheads.length > 0 && (
        <Panel title="Studio overheads" testid={`${testid}-overheads`}>
          {report.overheads.map((o) => <div key={o.category} className="flex justify-between border-b border-zinc-100 py-1.5 text-sm"><span>{o.category}</span><span>{inr(o.amount)}</span></div>)}
        </Panel>
      )}
    </div>
  );
}

function UploadCard({ onParsed }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", f);
    try { onParsed((await api.post("/finance-import/preview", fd)).data); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not read the sheet"); }
    setBusy(false);
  };
  return (
    <Panel testid="fin-upload-card" title="Upload your studio finance sheet">
      <p className="text-sm text-zinc-500">Upload an Excel (.xlsx) or CSV file. Columns like Date, Project, Client, Type, Category, Vendor, Amount, GST, Debit / Credit are detected automatically. For best results start from the template.</p>
      <div className="mt-4 flex flex-wrap gap-2">
        <input ref={ref} type="file" accept=".xlsx,.xlsm,.csv" className="hidden" data-testid="fin-upload-input" onChange={pick} />
        <Button data-testid="fin-upload-button" disabled={busy} onClick={() => ref.current.click()}>{busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Upload className="mr-1 h-4 w-4" />}{busy ? "Reading sheet…" : "Upload sheet"}</Button>
        <Button data-testid="fin-template-button" variant="outline" onClick={() => downloadBlob(api.get("/finance-import/template", { responseType: "blob" }), "template.xlsx")}><FileSpreadsheet className="mr-1 h-4 w-4" />Download template</Button>
      </div>
    </Panel>
  );
}

function ProjectResolver({ unmatched, projects, map, setMap }) {
  if (!unmatched.length) return null;
  return (
    <Panel testid="fin-unmatched" title={`Projects not found in the CRM (${unmatched.length})`}
      action={<Button data-testid="fin-create-all-new" size="sm" variant="outline" onClick={() => setMap((m) => ({ ...m, ...Object.fromEntries(unmatched.map((n) => [n, NEW])) }))}>Create all as new projects</Button>}>
      <div className="space-y-2">
        {unmatched.map((n) => (
          <div key={n} className="flex flex-wrap items-center justify-between gap-2 text-sm">
            <span className="font-semibold text-zinc-900">{n}</span>
            <select data-testid={`fin-map-${n}`} value={map[n] || ""} onChange={(e) => setMap((m) => ({ ...m, [n]: e.target.value }))} className="h-9 min-w-[240px] rounded-lg border border-zinc-200 px-2 text-sm">
              <option value="">Choose…</option>
              <option value={NEW}>{`+ Create new project “${n}”`}</option>
              {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        ))}
      </div>
    </Panel>
  );
}

function PreviewTable({ rows, setRows }) {
  const setType = (i, type) => setRows((r) => r.map((x, j) => (j === i ? { ...x, type } : x)));
  return (
    <Panel testid="fin-preview" title={`Sheet preview · ${rows.length} rows`}>
      <div className="thin-scroll max-h-[420px] overflow-auto">
        <table className="w-full min-w-[900px] text-xs">
          <thead className="sticky top-0 bg-white"><tr className="border-b border-zinc-200 text-left uppercase tracking-wider text-zinc-400">
            {["Row", "Date", "Project", "Type", "Category", "Party", "Description", "Amount", "GST", "Status"].map((h) => <th key={h} className="py-2 pr-2">{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.row ?? i} data-testid={`fin-preview-row-${i}`} className={`border-b border-zinc-100 ${r.issues.length ? "bg-amber-50/60" : ""}`}>
                <td className="py-1.5 pr-2 text-zinc-400">{r.row}</td><td className="pr-2">{r.date}</td><td className="pr-2 font-medium">{r.project || "—"}</td>
                <td className="pr-2"><select data-testid={`fin-type-${i}`} value={r.type} onChange={(e) => setType(i, e.target.value)} className="rounded border border-zinc-200 px-1 py-0.5">{TYPES.map((t) => <option key={t}>{t}</option>)}</select></td>
                <td className="pr-2">{r.category}</td><td className="pr-2">{r.party}</td><td className="max-w-[200px] truncate pr-2" title={r.description}>{r.description}</td>
                <td className="pr-2 text-right">{inr(r.amount)}</td><td className="pr-2 text-right">{inr(r.gst)}</td>
                <td className="pr-2">{r.issues.length ? <span className="inline-flex items-center gap-1 text-amber-700"><AlertTriangle className="h-3 w-3" />{r.issues.join(", ")}</span> : r.payment_status}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

function ImportFlow({ onImported }) {
  const [sheet, setSheet] = useState(null);
  const [rows, setRows] = useState([]);
  const [map, setMap] = useState({});
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);
  const parsed = (d) => { setSheet(d); setRows(d.rows); setMap(d.matches); setResult(null); };
  const missing = (sheet?.unmatched || []).filter((n) => !map[n]);
  const run = async (mode) => {
    if (mode === "import" && missing.length) { toast.error(`Choose a project for: ${missing.join(", ")}`); return; }
    setBusy(mode);
    try {
      const { data } = await api.post("/finance-import/commit", { rows, mode, project_map: map, file_name: sheet.file_name });
      setResult({ ...data, mode });
      if (mode === "import") {
        const c = data.created;
        toast.success(`Imported: ${c.vendor_expenses} vendor, ${c.internal_expenses} internal, ${c.deposits} client payments, ${c.studio_expenses} overheads${c.projects ? `, ${c.projects} new projects` : ""}`);
        onImported();
      }
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setBusy("");
  };
  if (result) {
    return (
      <div className="space-y-4">
        <Button data-testid="fin-start-over" variant="outline" size="sm" onClick={() => { setSheet(null); setResult(null); }}>Upload another sheet</Button>
        <ReportView report={result.report} rows={result.mode === "report" ? rows : undefined} title={result.mode === "import" ? `Studio report after import (${result.batch})` : `Report from ${sheet.file_name} (CRM unchanged)`} />
      </div>
    );
  }
  return (
    <div className="space-y-5">
      <UploadCard onParsed={parsed} />
      {sheet && <>
        <div className="text-xs text-zinc-500" data-testid="fin-detected-columns">Detected columns: {Object.entries(sheet.columns).map(([k, v]) => `${v} → ${k}`).join(" · ")}</div>
        <PreviewTable rows={rows} setRows={setRows} />
        <ProjectResolver unmatched={sheet.unmatched} projects={sheet.projects} map={map} setMap={setMap} />
        <Panel testid="fin-actions" title="What should happen with this sheet?">
          <div className="flex flex-wrap gap-2">
            <Button data-testid="fin-import-button" disabled={!!busy} onClick={() => run("import")}>{busy === "import" && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Import into CRM & build report</Button>
            <Button data-testid="fin-report-only-button" variant="outline" disabled={!!busy} onClick={() => run("report")}>{busy === "report" && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}Build report only (don't change CRM)</Button>
          </div>
          <p className="mt-2 text-xs text-zinc-500">Import adds rows as vendor expenses, internal expenses, client payments, project contract values and studio overheads, so Budget, P&L and reports update automatically. You can undo an import later.</p>
        </Panel>
      </>}
    </div>
  );
}

function ImportHistory({ refresh, onUndone }) {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/finance-imports").then((r) => setList(r.data)).catch(() => {}); }, [refresh]);
  if (!list.length) return null;
  const undo = async (b) => {
    if (!window.confirm(`Undo import ${b.id}? This deletes all ${b.rows} records it created.`)) return;
    try { await api.delete(`/finance-imports/${b.id}`); toast.success("Import undone"); onUndone(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  return (
    <Panel testid="fin-import-history" title="Previous imports">
      {list.map((b) => (
        <div key={b.id} data-testid={`fin-import-${b.id}`} className="flex flex-wrap items-center justify-between gap-2 border-b border-zinc-100 py-2 text-sm last:border-0">
          <div><div className="font-semibold">{b.file_name || b.id}</div><div className="text-xs text-zinc-500">{b.id} · {b.rows} rows · {b.created_at.slice(0, 16).replace("T", " ")} by {b.by}</div></div>
          {user.role === "admin" && <Button data-testid={`fin-undo-${b.id}`} size="sm" variant="ghost" className="text-rose-600" onClick={() => undo(b)}><Undo2 className="mr-1 h-3.5 w-3.5" />Undo</Button>}
        </div>
      ))}
    </Panel>
  );
}

function CrmReport({ refresh }) {
  const [report, setReport] = useState(null);
  useEffect(() => { api.get("/finance-report").then((r) => setReport(r.data)).catch(() => setReport(false)); }, [refresh]);
  if (report === null) return <div className="text-sm text-zinc-400">Loading report…</div>;
  if (!report) return <div className="text-sm text-rose-600">You don't have access to finance data.</div>;
  return <ReportView testid="crm-report" canEmail report={report} title="Overall studio profit & expenses (all CRM data)" />;
}

export default function FinanceSheet() {
  const [tab, setTab] = useState("import");
  const [refresh, setRefresh] = useState(0);
  const bump = () => setRefresh((n) => n + 1);
  return (
    <>
      <PageHeader num="19" title="Studio Finance Sheet" subtitle="Upload your studio finance sheet to auto-build profit and overall expense reports — imported into the CRM or as a standalone report." />
      <PillTabs testid="fin-tab" value={tab} onChange={setTab} tabs={[["import", "Import sheet"], ["report", "Studio P&L report"]]} />
      {tab === "import" ? <div className="space-y-5"><ImportFlow onImported={bump} /><ImportHistory refresh={refresh} onUndone={bump} /></div> : <CrmReport refresh={refresh} />}
    </>
  );
}
